import React, { useState, useEffect } from 'react';
import './ColorVarianceModal.css';

const ColorVarianceModal = ({ model, isOpen, onClose }) => {
  const [linkedColors, setLinkedColors] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [selectedColor, setSelectedColor] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (model && isOpen) {
      fetchLinkedColors();
      fetchAvailableColors();
    }
  }, [model, isOpen]);

  const fetchLinkedColors = async () => {
    try {
      const response = await fetch(`/api/models/${model.asset1_id}/linked-colors`);
      const data = await response.json();
      
      setLinkedColors(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Error fetching linked colors:', error);
      setLinkedColors([]);
    }
  };

  const fetchAvailableColors = async () => {
    try {
      const response = await fetch('/api/colors');
      const data = await response.json();
      
      setAvailableColors(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Error fetching available colors:', error);
      setAvailableColors([]);
    }
  };

  const handleAddColor = async () => {
    if (!selectedColor) return;
    setLoading(true);
    try {
      const response = await fetch(`/api/models/${model.asset1_id}/colors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset2_id: selectedColor }),
      });

      if (response.ok) {
        setSelectedColor('');
        fetchLinkedColors(); 
      }
    } catch (error) {
      console.error('Error adding color:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveColor = async (colorId) => {
    if (!window.confirm('Remove this color variance?')) return;
    try {
      const response = await fetch(
        `/api/models/${model.asset1_id}/colors/${colorId}`,
        { method: 'DELETE' }
      );
      if (response.ok) fetchLinkedColors();
    } catch (error) {
      console.error('Error removing color:', error);
    }
  };

  if (!model || !isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Color Variance - {model.asset1_name}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="add-color-section">
            <h3>Add Color Variance</h3>
            <div className="add-color-form">
              <select
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                disabled={loading}
              >
                <option value="">Select a color...</option>
                {availableColors
                  .filter(ac => !linkedColors.some(lc => lc.id === ac.id))
                  .map((color) => (
                    <option key={color.id} value={color.id}>
                      {color.name} {color.number ? `(${color.number})` : ''}
                    </option>
                  ))}
              </select>
              <button onClick={handleAddColor} disabled={loading || !selectedColor} className="btn-add">
                {loading ? 'Adding...' : 'Add Color'}
              </button>
            </div>
          </div>

          <div className="linked-colors-section">
            <h3>Current Color Variances ({linkedColors.length})</h3>
            {linkedColors.length === 0 ? (
              <p className="no-data">No colors linked to this model yet.</p>
            ) : (
              <div className="colors-list">
                {linkedColors.map((color) => (
                  <div key={color.id} className="color-item">
                    <div className="color-info">
                      <span className="color-name">{color.name}</span>
                      {color.number && <span className="color-code">({color.number})</span>}
                    </div>
                    <button onClick={() => handleRemoveColor(color.id)} className="btn-remove">
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn-close">Close</button>
        </div>
      </div>
    </div>
  );
};

export default ColorVarianceModal;